if (!customElements.get('tpg-video-banner')) {
  class TpgVideoBanner extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.video = this.querySelector('[data-tpg-video]');
      this.toggle = this.querySelector('[data-tpg-video-toggle]');

      if (!this.video) return;

      this.initialized = true;
      this.autoplay = this.dataset.autoplay === 'true';
      this.userPaused = !this.autoplay;
      this.allowReducedMotionPlayback = false;
      this.isVisible = true;
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.breakpointPreference = window.matchMedia('(max-width: 749px)');

      this.handleToggle = this.handleToggle.bind(this);
      this.handlePlaybackChange = this.handlePlaybackChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);

      this.classList.add('tpg-video-banner--js');
      this.video.muted = true;

      this.toggle?.addEventListener('click', this.handleToggle);
      this.video.addEventListener('play', this.handlePlaybackChange);
      this.video.addEventListener('pause', this.handlePlaybackChange);
      this.video.addEventListener('ended', this.handlePlaybackChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if (this.motionPreference.addEventListener) {
        this.motionPreference.addEventListener('change', this.handleMotionChange);
        this.breakpointPreference.addEventListener('change', this.handleBreakpointChange);
      } else {
        this.motionPreference.addListener(this.handleMotionChange);
        this.breakpointPreference.addListener(this.handleBreakpointChange);
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.handleIntersection, { threshold: 0.05 });
        this.observer.observe(this);
      }

      this.updatePlayback();
      this.syncState();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.toggle?.removeEventListener('click', this.handleToggle);
      this.video?.removeEventListener('play', this.handlePlaybackChange);
      this.video?.removeEventListener('pause', this.handlePlaybackChange);
      this.video?.removeEventListener('ended', this.handlePlaybackChange);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

      if (this.motionPreference?.removeEventListener) {
        this.motionPreference.removeEventListener('change', this.handleMotionChange);
        this.breakpointPreference?.removeEventListener('change', this.handleBreakpointChange);
      } else {
        this.motionPreference?.removeListener(this.handleMotionChange);
        this.breakpointPreference?.removeListener(this.handleBreakpointChange);
      }

      this.observer?.disconnect();
      this.initialized = false;
    }

    handleToggle() {
      if (this.video.paused) {
        this.userPaused = false;
        this.allowReducedMotionPlayback = true;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    handlePlaybackChange() {
      this.syncState();
    }

    handleMotionChange(event) {
      if (event.matches) {
        this.allowReducedMotionPlayback = false;
      }

      this.updatePlayback();
    }

    handleBreakpointChange() {
      this.video.load();
      this.syncState();
      this.updatePlayback();
    }

    handleVisibilityChange() {
      this.updatePlayback();
    }

    handleIntersection(entries) {
      const entry = entries[0];
      this.isVisible = Boolean(entry?.isIntersecting);
      this.updatePlayback();
    }

    updatePlayback() {
      const motionAllowed = !this.motionPreference.matches || this.allowReducedMotionPlayback;
      const shouldPlay =
        this.autoplay &&
        !this.userPaused &&
        motionAllowed &&
        this.isVisible &&
        document.visibilityState !== 'hidden';

      if (shouldPlay) {
        this.play();
      } else {
        this.video.pause();
      }
    }

    play() {
      const playPromise = this.video.play();

      if (playPromise?.catch) {
        playPromise.catch(() => this.syncState());
      }
    }

    syncState() {
      const isPlaying = !this.video.paused && !this.video.ended;
      this.dataset.videoState = isPlaying ? 'playing' : 'paused';

      if (!this.toggle) return;

      const playIcon = this.toggle.querySelector('[data-tpg-play-icon]');
      const pauseIcon = this.toggle.querySelector('[data-tpg-pause-icon]');
      const label = isPlaying ? this.toggle.dataset.pauseLabel : this.toggle.dataset.playLabel;

      this.toggle.setAttribute('aria-label', label || 'Play video');
      if (playIcon) playIcon.hidden = isPlaying;
      if (pauseIcon) pauseIcon.hidden = !isPlaying;
    }
  }

  customElements.define('tpg-video-banner', TpgVideoBanner);
}
